import { type NavigateOptions, useNavigate } from "@tanstack/react-router";
import { isHTTPError } from "ky";
import { useCallback } from "react";
import type { Task } from "@/lib/api.types";
import { tasksQueryOptions } from "@/lib/data";
import {
  ensureConsumerQueryData,
  queryClient,
  retryIfCancelled,
} from "@/lib/query-client";

const CLOSED_TASK_STATUSES = ["completed", "skipped"] as const;

export function isClosedTask(task: Task): boolean {
  return (CLOSED_TASK_STATUSES as readonly string[]).includes(task.status);
}

export function isSkippedTask(task: Task): boolean {
  return task.status === "skipped";
}

export function getTaskStatusLabel(status: string) {
  if (status === "completed") return "Complete";
  if (status === "skipped") return "Skipped";
  return "In progress";
}

/**
 * The applicant's next stop in the journey: the first open task after
 * `afterTaskId`, falling back to the first open task anywhere else in the list
 * so an earlier unfinished task doesn't strand them on the list page.
 *
 * `afterTaskId` is always excluded. The backend decides whether a submission
 * closes a task, and some don't close on the first pass — without the exclusion
 * a still-open task would send the applicant straight back into itself.
 *
 * With no `afterTaskId` the same scan yields the top-most open task, which is
 * where every sign-in lands. Task order is the server's array order; there is
 * no position field to sort on.
 */
function findNextOpenTask(
  tasks: Task[],
  afterTaskId?: string,
): Task | undefined {
  const isCandidate = (task: Task) =>
    task.id !== afterTaskId && !isClosedTask(task);
  const currentIndex = afterTaskId
    ? tasks.findIndex((t) => t.id === afterTaskId)
    : -1;

  return (
    tasks.slice(currentIndex + 1).find(isCandidate) ?? tasks.find(isCandidate)
  );
}

/**
 * Where to send the applicant next: the task itself, or the list when there is
 * nothing left to do (it already renders the "You're all set!" state).
 */
export function taskJourneyTarget(
  tasks: Task[],
  afterTaskId?: string,
): NavigateOptions {
  const next = findNextOpenTask(tasks, afterTaskId);

  return next
    ? { to: "/steps/$taskId", params: { taskId: next.id } }
    : { to: "/steps" };
}

/**
 * `staleTime: 0` skips the cache and shares any refetch already in flight. That
 * refetch is normally the one the write's own invalidation started, so it adds
 * no request. A refetch that began before the write landed could be shared if
 * the write didn't invalidate; every task-closing write does today, and
 * invalidation cancels an older in-flight refetch.
 */
async function fetchFreshTasks(): Promise<Task[]> {
  const data = await retryIfCancelled(tasksQueryOptions.queryKey, () =>
    queryClient.fetchQuery({ ...tasksQueryOptions, staleTime: 0 }),
  );
  return data?.tasks ?? [];
}

/**
 * Finds a task by id for the detail route. The cached list is tried first; an
 * id it hasn't seen yet, such as a reissued task, gets one fresh read before
 * the route gives up. Resolves `undefined` when the id is still missing or the
 * session is gone (401); any other error propagates.
 */
export async function ensureTask(taskId: string): Promise<Task | undefined> {
  const cached = await ensureConsumerQueryData(tasksQueryOptions);
  const known = cached?.tasks.find((t) => t.id === taskId);
  if (known || !cached) return known;

  try {
    return (await fetchFreshTasks()).find((t) => t.id === taskId);
  } catch (error) {
    if (isHTTPError(error) && error.response.status === 401) return undefined;
    throw error;
  }
}

/**
 * Decides where the Applicant goes after submitting `taskId`, from one fresh
 * read of the server's task list. `null` means stay: the task is a Repeating
 * task and the backend hasn't closed it yet.
 *
 * Callers reach here after the work that matters has already succeeded — a
 * verified code, an accepted invite, a saved task — so a failed refetch must
 * not surface as "that didn't work". The list is the safe destination: it has
 * its own auth guard and its own loading and error states, and the query cache
 * still reports the failure to Sentry.
 */
async function resolveAdvance(
  taskId: string | undefined,
  repeatsUntilClosed: boolean,
): Promise<NavigateOptions | null> {
  try {
    const tasks = await fetchFreshTasks();
    const current = tasks.find((t) => t.id === taskId);
    if (repeatsUntilClosed && current && !isClosedTask(current)) return null;
    return taskJourneyTarget(tasks, taskId);
  } catch {
    return { to: "/steps" };
  }
}

/**
 * Resolves the target against the server's current task list, for callers that
 * navigate themselves (see `resolveAdvance` for the read and its failure policy).
 */
export async function resolveTaskJourneyTarget(
  afterTaskId?: string,
): Promise<NavigateOptions> {
  return (await resolveAdvance(afterTaskId, false)) ?? { to: "/steps" };
}

/**
 * Moves the Applicant on from `taskId` to the Next task. For a Repeating task
 * (`repeatsUntilClosed`) that is still Open it stays put instead, so the form
 * can take the next entry. Never rejects.
 */
export function useAdvanceFromTask(
  taskId: string | undefined,
  options: { repeatsUntilClosed?: boolean } = {},
): () => Promise<void> {
  const navigate = useNavigate();
  const { repeatsUntilClosed = false } = options;

  return useCallback(async () => {
    const target = await resolveAdvance(taskId, repeatsUntilClosed);
    if (target) await navigate(target);
  }, [taskId, repeatsUntilClosed, navigate]);
}
